import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  withCredentials: true,
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
});

/** Public health only: no JWT interceptors so polling works during token/refresh issues or deploys. */
const healthHttp = axios.create({
  withCredentials: true,
  baseURL: API_BASE,
  timeout: 12000,
  headers: { Accept: 'application/json' },
});

const authClient = axios.create({
  withCredentials: true,
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
});

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

const API_LOG_EVENT = 'ventus-api-log';
const MAX_API_LOGS = 200;

function readApiLogs() {
  return Array.isArray(window.__ventusApiLogs) ? window.__ventusApiLogs : [];
}

function writeApiLogs(next) {
  window.__ventusApiLogs = next.slice(0, MAX_API_LOGS);
}

function pushApiLog(entry) {
  const current = readApiLogs();
  const next = [entry, ...current];
  writeApiLogs(next);
  window.dispatchEvent(new CustomEvent(API_LOG_EVENT, { detail: entry }));
}

export function getApiLogs() {
  return readApiLogs();
}

export function clearApiLogs() {
  writeApiLogs([]);
}

export const securityApi = {
  bootstrapCsrf: () => api.get('/csrf'),
};

/** Single in-flight refresh so concurrent 401s share one /auth/refresh call. */
let refreshChain = null;

/**
 * ASA-180 fase 3b: vernieuw de sessie via de httpOnly refresh-cookie (geen localStorage).
 * De backend zet bij succes nieuwe httpOnly-cookies. Geeft true op succes.
 */
export async function refreshTokensFromStorage() {
  if (refreshChain) {
    return refreshChain;
  }
  refreshChain = (async () => {
    try {
      const { data } = await authClient.post('/auth/refresh', {});
      const looksJwt = typeof data?.token === 'string' && data.token.split('.').length === 3;
      if (!looksJwt) {
        pushApiLog({ at: new Date().toISOString(), ok: false, stage: 'refresh', method: 'POST', url: '/auth/refresh', status: null, message: 'Refresh returned invalid token shape', refreshAttempted: true, refreshSucceeded: false, data: null });
        return false;
      }
      window.dispatchEvent(new Event('ventus-auth-refreshed'));
      pushApiLog({ at: new Date().toISOString(), ok: true, stage: 'refresh', method: 'POST', url: '/auth/refresh', status: 200, message: 'Refresh succeeded (cookie)', refreshAttempted: true, refreshSucceeded: true, data: null });
      return true;
    } catch {
      pushApiLog({ at: new Date().toISOString(), ok: false, stage: 'refresh', method: 'POST', url: '/auth/refresh', status: null, message: 'Refresh request failed', refreshAttempted: true, refreshSucceeded: false, data: null });
      return false;
    }
  })().finally(() => {
    refreshChain = null;
  });
  return refreshChain;
}

// Log each request; the httpOnly auth cookie is sent automatically (same-origin, withCredentials).
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = readCookie('XSRF-TOKEN');
    if (token) {
      config.headers = config.headers || {};
      config.headers['X-XSRF-TOKEN'] = token;
    }
  }
  pushApiLog({ at: new Date().toISOString(), ok: null, stage: 'request', method: (config.method || 'get').toUpperCase(), url: config.url || '', status: null, message: null, authRetry: Boolean(config._authRetry), data: null });
  return config;
});

// On 401: refresh the session via the cookie once and retry. On failure, signal logout.
api.interceptors.response.use(
  (response) => {
    pushApiLog({ at: new Date().toISOString(), ok: true, stage: 'response', method: (response.config?.method || 'get').toUpperCase(), url: response.config?.url || '', status: response.status, message: response.data?.message || null, authRetry: Boolean(response.config?._authRetry), data: response.data ?? null });
    return response;
  },
  async (error) => {
    const { response, config } = error;
    pushApiLog({ at: new Date().toISOString(), ok: false, stage: 'response', method: (config?.method || 'get').toUpperCase(), url: config?.url || '', status: response?.status ?? null, message: response?.data?.message || error?.message || 'Request failed', authRetry: Boolean(config?._authRetry), data: response?.data ?? null });
    if (!config || config._authRetry) {
      return Promise.reject(error);
    }
    if (response?.status !== 401) {
      return Promise.reject(error);
    }
    const path = config.url || '';
    if (path.startsWith('/auth/')) {
      return Promise.reject(error);
    }
    config._authRetry = true;
    const ok = await refreshTokensFromStorage();
    if (!ok) {
      window.dispatchEvent(new Event('ventus-auth-expired'));
      return Promise.reject(error);
    }
    return api.request(config);
  }
);

export const authApi = {
  login: (email, password) =>
    authClient.post('/auth/login', { email, password }),
  register: (email, password, fullName) =>
    authClient.post('/auth/register', { email, password, fullName }),
  requestCode: (email, fullName = '') =>
    authClient.post('/auth/request-code', { email, fullName }),
  verifyCode: (email, code, fullName = '') =>
    authClient.post('/auth/verify-code', { email, code, fullName }),
  refresh: (refreshToken) =>
    authClient.post('/auth/refresh', { refreshToken }),
};

// ─── Workspaces ─────────────────────────────────────
export const workspaceApi = {
  list: () => api.get('/workspaces'),
  get: (id) => api.get(`/workspaces/${id}`),
  listMembers: (id) => api.get(`/workspaces/${id}/members`),
  listInviteCandidates: (id) => api.get(`/workspaces/${id}/invite-candidates`),
  create: (data) => api.post('/workspaces', data),
  update: (id, data) => api.put(`/workspaces/${id}`, data),
  exportBackup: (id) =>
    api.get(`/workspaces/${id}/backup`, { responseType: 'blob' }),
  delete: (id) => api.delete(`/workspaces/${id}`),
  inviteMember: (id, email, role) =>
    api.post(`/workspaces/${id}/members`, { email, role }),
  removeMember: (id, userId) =>
    api.delete(`/workspaces/${id}/members/${userId}`),
};

// ─── Documents ──────────────────────────────────────
export const documentApi = {
  list: (workspaceId) =>
    api.get(`/workspaces/${workspaceId}/documents`),
  get: (workspaceId, docId) =>
    api.get(`/workspaces/${workspaceId}/documents/${docId}`),
  versions: (workspaceId, docId) =>
    api.get(`/workspaces/${workspaceId}/documents/${docId}/versions`),
  upload: (workspaceId, file, onProgress, supersedesDocumentId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (supersedesDocumentId != null) {
      formData.append('supersedesDocumentId', String(supersedesDocumentId));
    }
    return api.post(`/workspaces/${workspaceId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  uploadBatch: (workspaceId, files, onProgress) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post(`/workspaces/${workspaceId}/documents/batch`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  uploadText: (workspaceId, title, text) =>
    api.post(`/workspaces/${workspaceId}/documents/text`, { title, text }),
  getChunks: (workspaceId, docId) =>
    api.get(`/workspaces/${workspaceId}/documents/${docId}/chunks`),
  /** Original file (auth via api interceptor). inline: open in browser tab for PDF etc. */
  getFileBlob: (workspaceId, docId, inline = false) =>
    api.get(`/workspaces/${workspaceId}/documents/${docId}/file`, {
      params: inline ? { inline: true } : {},
      responseType: 'blob',
    }),
  getExtractedText: (workspaceId, docId) =>
    api.get(`/workspaces/${workspaceId}/documents/${docId}/extracted-text`, {
      responseType: 'text',
    }),
  delete: (workspaceId, docId) =>
    api.delete(`/workspaces/${workspaceId}/documents/${docId}`),
  search: (workspaceId, q, limit = 25) =>
    api.get(`/workspaces/${workspaceId}/document-search`, { params: { q, limit } }),
  patch: (workspaceId, docId, body) =>
    api.patch(`/workspaces/${workspaceId}/documents/${docId}`, body),
  reprocess: (workspaceId, docId) =>
    api.post(`/workspaces/${workspaceId}/documents/${docId}/reprocess`),
};

// ─── Q&A ────────────────────────────────────────────
export const qaApi = {
  ask: (workspaceId, question) =>
    api.post(`/workspaces/${workspaceId}/qa`, { question }),
  list: (workspaceId) =>
    api.get(`/workspaces/${workspaceId}/qa`),
  get: (workspaceId, questionId) =>
    api.get(`/workspaces/${workspaceId}/qa/${questionId}`),
  claim: (workspaceId, questionId) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/claim`),
  releaseAssignment: (workspaceId, questionId) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/release-assignment`),
  vote: (workspaceId, questionId, upvote) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/vote`, { upvote }),
  togglePin: (workspaceId, questionId) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/pin`),
  setPrivacy: (workspaceId, questionId, privateQuestion) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/privacy`, { privateQuestion }),
  approvePrivateVisibility: (workspaceId, questionId) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/privacy/approve`),
  approve: (workspaceId, questionId) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/approve`),
  agendaProposal: (workspaceId, questionId) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/agenda/proposal`),
  createAgendaItem: (workspaceId, questionId, payload) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/agenda-items`, payload),
  listAgendaItems: (workspaceId, questionId) =>
    api.get(`/workspaces/${workspaceId}/qa/${questionId}/agenda-items`),
  listWorkspaceAgendaItems: (workspaceId) =>
    api.get(`/workspaces/${workspaceId}/qa/agenda-items`),
  todoSuggestions: (workspaceId, questionId) =>
    api.post(`/workspaces/${workspaceId}/qa/${questionId}/todo-suggestions`),
  previewAgendaImport: (source, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/me/agenda-import/preview', formData, {
      params: source ? { source } : {},
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importAgendaToWorkspaces: (payload) =>
    api.post('/me/agenda-import/workspaces', payload),
  exportPdfReport: (workspaceId, payload) =>
    api.post(`/workspaces/${workspaceId}/qa/reports/pdf`, payload, { responseType: 'blob' }),
  exportMarkdown: (workspaceId, questionId) =>
    api.get(`/workspaces/${workspaceId}/qa/${questionId}/export`, { responseType: 'blob' }),
  /** All agenda items across workspaces you can access (includes workspaceName, questionPreview). */
  listMyAgendaItems: () => api.get('/me/agenda-items'),
};

// ─── Legal Research ────────────────────────────────
export const legalApi = {
  searchLaws: (q) => api.get('/legal/laws', { params: q ? { q } : {} }),
  getArticle: (lawId, articleNumber) => api.get(`/legal/laws/${lawId}/articles/${articleNumber}`),
  searchCases: (q) => api.get('/legal/cases', { params: q ? { q } : {} }),
  research: (payload) => api.post('/legal/research', payload),
};

// ─── Notifications ────────────────────────────────
export const notificationApi = {
  list: () => api.get('/notifications'),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// ─── Comments ───────────────────────────────────────
export const commentApi = {
  list: (questionId) =>
    api.get(`/questions/${questionId}/comments`),
  add: (questionId, content, type = 'COMMENT') =>
    api.post(`/questions/${questionId}/comments`, { content, type }),
};

// ─── Health / build info (public) ───────────────────
export const healthApi = {
  get: () => healthHttp.get('/health'),
};

// ─── Admin ──────────────────────────────────────────
export const adminApi = {
  dashboard: () => api.get('/admin/dashboard'),
  auditLog: (page = 0, size = 50) =>
    api.get(`/admin/audit?page=${page}&size=${size}`),
  workspaces: () => api.get('/admin/workspaces'),
  users: () => api.get('/admin/users'),
  setUserRole: (userId, role) =>
    api.patch(`/admin/users/${userId}/role`, { role }),
  /** Recent JVM log lines (admin-only; buffer since backend start). */
  serverLogs: (limit = 2000, q) =>
    api.get('/admin/server-logs', { params: { limit, ...(q ? { q } : {}) } }),
  purgeMockLegalData: () => api.post('/admin/legal/cleanup/mock-data'),
};

export default api;
